import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { AssignmentSettings, AssignmentSettingsResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { assignmentSettingsQuery, useSaveAssignmentSettings, useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { MAX_ASSIGNMENT_WEIGHT, weightOf } from "@gioia365/domain/assignment";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import {
  assignmentProblem,
  describeAssignmentChanges,
  draftNext,
  draftOf,
  problemText,
  saveBodyOf,
  setWeight,
  singleFor,
  togglePerson,
  type AssignmentEdits,
} from "./assignment-draft";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import "./assignment.css";

// Impostazioni → «Assegnazione dei Lead» (piano M6, Task 13; D16; mock-up `regole.html?sezione=assegnazione`): a chi va un Lead nuovo
// che entra senza un owner scelto. I tre modi (a turno fra le persone scelte, tutti a una persona, a nessuno), chi entra nel turno,
// «I prossimi 6 Lead nuovi» e «Un Lead che torna». Sul modello di «Sequenza di chiamate»: una bozza (i soli campi toccati), la barra
// delle modifiche, «Salva…» con «Cosa cambia per il team» e una sola richiesta con la regola intera e la versione letta; una versione
// cambiata nel frattempo (`409 version_conflict`, o la rilettura che la porta) rimette le modifiche sopra la regola nuova e lo dice.
// La vede ogni persona attiva; la cambia chi ha `settings.assignment` (`canEdit` del server), mai il Super admin di Gioia365
// dall'interfaccia (questione 43). «Un Lead che torna» in M6 si salva e basta: la pagina lo dice, lo userà l'ingresso da GoHighLevel.

// Il segnaposto dei nomi nella frase dei prossimi Lead: i nomi vanno in grassetto, come nel mock-up, senza spezzare la frase in
// pezzi del catalogo (l'ordine delle parole è della lingua).
const NAMES = "\u0001";

export function AssignmentPage() {
  return (
    <SettingsLayout section="assignment">
      <AssignmentSection />
    </SettingsLayout>
  );
}

function AssignmentSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const rule = useQuery(assignmentSettingsQuery(tab));
  const organization = access.context.organization.name;
  const allowed = access.can("settings.assignment") && !access.isPlatform;
  const editable = allowed && rule.data?.canEdit === true;

  let body: ReactNode;
  if (rule.data) {
    body = <AssignmentEditor base={rule.data} editable={editable} />;
  } else if (rule.isError) {
    body = (
      <div className="g-assignment__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, rule.error)}
        </Reason>
        <Button onClick={() => void rule.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-assignment__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("assignmentSettings.about")}>
        <p>{t("assignmentSettings.aboutText")}</p>
        <p>{t("assignmentSettings.aboutTeam")}</p>
        <p>{t("assignmentSettings.aboutNow")}</p>
      </AboutBox>
      {!allowed || (rule.data && !rule.data.canEdit) ? (
        <Reason>{access.isPlatform ? t("assignmentSettings.readOnlyPlatform", { organization }) : t("assignmentSettings.readOnly", { organization })}</Reason>
      ) : null}
      {body}
    </>
  );
}

/** «I prossimi 6 Lead nuovi: …» con i nomi in grassetto, oppure la frase di quando i Lead arrivano senza owner. */
function NextLeads({ names }: { names: readonly string[] }) {
  const t = useT();
  if (names.length === 0) return <>{t("assignmentSettings.nextNone")}</>;
  const [before = "", after = ""] = t("assignmentSettings.nextLeads", { names: NAMES }).split(NAMES);
  return (
    <>
      {before}
      <strong>{names.join(", ")}</strong>
      {after}
    </>
  );
}

type EditorProps = { base: AssignmentSettingsResponse; editable: boolean };

function AssignmentEditor({ base, editable }: EditorProps) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const saveRule = useSaveAssignmentSettings(tab);
  const baseId = useId();
  const [edits, setEdits] = useState<AssignmentEdits>({});
  // La finestra «Cosa cambia per il team», con il rifiuto del server se il salvataggio si è fermato.
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  // Un conflitto di versione: la pagina lo dice sopra la bozza.
  const [notice, setNotice] = useState<Feedback | null>(null);
  // La versione su cui la bozza è stata pensata: se la rilettura ne porta un'altra mentre ci sono modifiche, le modifiche restano sopra
  // la regola nuova e la pagina lo dice, come per un 409 (il modello di «Sequenza di chiamate»).
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);

  const saved = base.settings;
  const { candidates } = base;
  // Senza il permesso (che può sparire con un contesto riletto) si vede il salvato, non una bozza che non si può salvare.
  const draft = useMemo(() => draftOf(saved, editable ? edits : {}), [saved, editable, edits]);
  const changes = describeAssignmentChanges(saved, draft, candidates, t);
  const dirty = editable && changes.length > 0;
  const blocker = useLeaveGuard(dirty);
  const problemCode = assignmentProblem(draft, candidates);
  const problem = problemCode ? problemText(t, problemCode) : null;
  const hasEdits = editable && Object.keys(edits).length > 0;
  // «I prossimi 6 Lead nuovi» del server per la regola salvata; con una bozza, lo stesso calcolo sulla bozza.
  const next = useMemo(() => (dirty ? draftNext(draft, candidates, base.lastServed, base.servedInRow) : base.next), [dirty, draft, candidates, base.lastServed, base.servedInRow, base.next]);

  useEffect(() => {
    if (!hasEdits) {
      if (draftVersion !== null) setDraftVersion(null);
      return;
    }
    if (draftVersion === null) {
      setDraftVersion(base.version);
      return;
    }
    if (base.version === draftVersion) return;
    setDraftVersion(base.version);
    // Il proprio salvataggio rilegge la regola mentre salva: quello non è un conflitto.
    if (savingNow.current) return;
    setConfirming(null);
    setNotice({ text: (tr) => tr("assignmentSettings.conflict") });
  }, [hasEdits, base.version, draftVersion]);

  const disabled = !editable || saving;

  function change(update: AssignmentEdits) {
    if (!editable || savingNow.current) return;
    setEdits((current) => ({ ...current, ...update }));
  }

  function setMode(mode: AssignmentSettings["mode"]) {
    // Con «Tutti a una persona» la persona c'è sempre, come il menu del mock-up: quella scelta, o la prima attiva.
    change({ mode, ...(mode === "single" ? { single: singleFor(draft, candidates) } : {}) });
  }

  function discard() {
    setEdits({});
    setNotice(null);
    toast.show(t("settings.draft.discarded"));
  }

  async function save() {
    if (!editable || savingNow.current || problemCode !== null || changes.length === 0) return;
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      await saveRule.mutateAsync(saveBodyOf(saved, draft, base.version));
      // La conferma si chiude prima dell'avviso.
      setConfirming(null);
      setEdits({});
      setNotice(null);
      toast.show(t("assignmentSettings.saved"), "success");
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // La regola si rilegge: le modifiche restano sopra quella nuova, da controllare.
        setConfirming(null);
        setNotice({ text: (tr) => tr("assignmentSettings.conflict") });
      } else {
        // La finestra resta con la bozza e dice perché: si torna alle modifiche o si riprova.
        setConfirming({ error: { text: (tr) => messageForError(tr, error) } });
      }
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setEdits({});
    if (blocker.status === "blocked") blocker.proceed();
  }

  const modes: Array<{ mode: AssignmentSettings["mode"]; label: string }> = [
    { mode: "round_robin", label: t("assignmentSettings.modeRoundRobin") },
    { mode: "single", label: t("assignmentSettings.modeSingle") },
    { mode: "manual", label: t("assignmentSettings.modeManual") },
  ];
  const returning: Array<{ value: AssignmentSettings["returning"]; label: string }> = [
    { value: "keep_owner", label: t("assignmentSettings.returningKeep") },
    { value: "as_new", label: t("assignmentSettings.returningAsNew") },
  ];
  const nameOf = (name: string, active: boolean) => (active ? name : t("assignmentSettings.suspended", { name }));
  const activeOptions = candidates
    .filter((candidate) => candidate.active)
    .map((candidate) => ({ value: candidate.membershipId, label: candidate.displayName }));
  // La persona unica salvata che oggi è sospesa si vede com'è, spenta e con «(sospeso)»: senza la sua voce il menu mostrerebbe la prima
  // persona attiva al suo posto, e la pagina direbbe una regola che non è quella salvata. Senza nessuna persona, il segnaposto.
  const chosenSingle = candidates.find((candidate) => candidate.membershipId === draft.single);
  const singleOptions = !chosenSingle
    ? [{ value: "", label: t("assignmentSettings.choosePerson") }, ...activeOptions]
    : chosenSingle.active
      ? activeOptions
      : [{ value: chosenSingle.membershipId, label: nameOf(chosenSingle.displayName, false), disabled: true }, ...activeOptions];

  return (
    <>
      {notice ? (
        <Reason tone="warning" role="alert">
          {notice.text(t)}
        </Reason>
      ) : null}

      <section className="g-assignment__section" aria-labelledby={`${baseId}-new`}>
        <h2 id={`${baseId}-new`}>{t("assignmentSettings.newLeads")}</h2>
        <fieldset className="g-assignment__choices">
          <legend className="g-visually-hidden">{t("assignmentSettings.how")}</legend>
          {modes.map((option) => (
            <label key={option.mode} className="g-assignment__choice" data-disabled={disabled ? "true" : undefined}>
              <input type="radio" name={`${baseId}-mode`} value={option.mode} checked={draft.mode === option.mode} disabled={disabled} onChange={() => setMode(option.mode)} />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>

        {draft.mode === "round_robin" ? (
          <fieldset className="g-assignment__people">
            <legend>{t("assignmentSettings.whoJoins")}</legend>
            {candidates.length === 0 ? <p className="g-assignment__muted">{t("assignmentSettings.noCandidates")}</p> : null}
            {candidates.map((candidate) => {
              const inTurn = draft.people.includes(candidate.membershipId);
              return (
                <div key={candidate.membershipId} className="g-assignment__person">
                  <Checkbox
                    id={`${baseId}-person-${candidate.membershipId}`}
                    label={nameOf(candidate.displayName, candidate.active)}
                    checked={inTurn}
                    // Chi è sospeso si vede, spuntato o no, ma non si sceglie: salta il turno da solo.
                    disabled={disabled || !candidate.active}
                    onChange={(on) => change({ people: togglePerson(draft.people, candidates, candidate.membershipId, on) })}
                  />
                  {inTurn ? (
                    // Il peso del turno (ASSEG-2): quanti Lead nuovi di fila, da 1 a 10. L'etichetta col nome la legge lo screen reader.
                    <Select
                      id={`${baseId}-weight-${candidate.membershipId}`}
                      label={t("assignmentSettings.weightLabel", { name: candidate.displayName })}
                      hideLabel
                      className="g-assignment__weight"
                      value={String(weightOf(draft, candidate.membershipId))}
                      onChange={(value) => change({ weights: setWeight(draft.weights, candidate.membershipId, Number(value)) })}
                      options={Array.from({ length: MAX_ASSIGNMENT_WEIGHT }, (_, index) => ({
                        value: String(index + 1),
                        label: t("assignmentSettings.weightOption", { count: String(index + 1) }),
                      }))}
                      disabled={disabled}
                    />
                  ) : null}
                </div>
              );
            })}
            <p className="g-assignment__muted">{t("assignmentSettings.weightNote")}</p>
            <p className="g-assignment__muted">{t("assignmentSettings.orderNote")}</p>
          </fieldset>
        ) : null}

        {draft.mode === "single" ? (
          <Select
            id={`${baseId}-single`}
            label={t("assignmentSettings.toWhom")}
            className="g-assignment__single"
            value={chosenSingle ? chosenSingle.membershipId : ""}
            onChange={(single) => change({ single: single === "" ? null : single })}
            options={singleOptions}
            disabled={disabled}
          />
        ) : null}

        <Reason tone="info">
          <NextLeads names={next.map((person) => person.displayName)} />
        </Reason>
      </section>

      <section className="g-assignment__section" aria-labelledby={`${baseId}-returning`}>
        <h2 id={`${baseId}-returning`}>{t("assignmentSettings.returning")}</h2>
        <fieldset className="g-assignment__choices">
          <legend className="g-assignment__legend">{t("assignmentSettings.returningLegend")}</legend>
          {returning.map((option) => (
            <label key={option.value} className="g-assignment__choice" data-disabled={disabled ? "true" : undefined}>
              <input
                type="radio"
                name={`${baseId}-returning`}
                value={option.value}
                checked={draft.returning === option.value}
                disabled={disabled}
                onChange={() => change({ returning: option.value })}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
        <p className="g-assignment__muted">{t("assignmentSettings.returningNote")}</p>
      </section>

      {dirty ? (
        <SaveBar
          summary={draftSummary(t, changes.length)}
          problem={problem ?? undefined}
          onDiscard={discard}
          onSave={() => setConfirming({ error: null })}
          saveLabel={t("settings.draft.save")}
          saving={saving}
        />
      ) : null}

      {confirming && dirty ? (
        <ConfirmChangesDialog changes={changes} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void save()} />
      ) : null}

      {blocker.status === "blocked" ? <LeaveDialog onStay={stay} onLeave={leave} /> : null}
    </>
  );
}
